import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { ApiError, api } from 'shared/api/client';
import { ReferenceDataEntry, documentKeys } from 'entities/document';
import type { EditorDocument, ReferenceCell, ReferenceDataValue } from 'entities/document';
import { tagQueryKeys, invalidateOnValueSave } from 'entities/tag';
import { i18n } from 'shared/i18n';
import { MissingSheet, parseSheetOptions, parseXlsxData } from './utils';
import type { XlsxFileData } from './utils';
import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { EventType, dispatchMessage } from 'shared/feedback/toasts';
import { referenceDataKeys } from './keys';
import type { paths } from 'shared/api/generated/api';
import { requireValue } from 'shared/lib/assert';
import { isRevisionMismatch } from 'shared/api/revision-mismatch';

type ReferenceDataPutBody = NonNullable<paths['/reference-data/']['put']['requestBody']>['content']['application/json'];
type ReferenceDataPostBody = NonNullable<paths['/reference-data/']['post']['requestBody']>['content']['application/json'];

export function useXlsxFile(fileRevisionId: string) {
  return useQuery({
    queryKey: referenceDataKeys.xlsxFile(fileRevisionId),

    queryFn: async () => {
      const { data } = await api.GET('/files/{id}', { params: { path: { id: fileRevisionId } } });
      return {
        selectedXlsx: data,
        sheetOptions: parseSheetOptions(data),
      };
    },

    enabled: !!fileRevisionId,
  });
}

export function useXlsxFiles(documentId: number | string) {
  return useQuery({
    queryKey: referenceDataKeys.xlsxFiles(documentId),

    queryFn: async () => {
      const { data } = await api.GET('/files/', { params: { query: { documentSuperId: Number(documentId), type: 'xlsx' } } });
      return data;
    },
  });
}

export function useReferenceDataByRevisionId(revisionId: string) {
  return useQuery({
    queryKey: referenceDataKeys.byRevision(revisionId),

    queryFn: async () => {
      const { data } = await api.GET('/reference-data/{id}', { params: { path: { id: revisionId } } });
      return data;
    },

    enabled: !!revisionId,
  });
}

const tableSaves = new Map<string, { queue: Promise<unknown>; pending: number; revisionId?: string }>();

export function useUpdateReferenceData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ document, referenceData }: { document?: EditorDocument; referenceData: ReferenceDataValue }) => {
      const key = String(referenceData.superId);
      const save = tableSaves.get(key) ?? { queue: Promise.resolve(), pending: 0 };
      tableSaves.set(key, save);
      save.pending += 1;
      const run = save.queue
        .catch(() => undefined)
        .then(async () => {
          const baseRevisionId =
            save.revisionId ?? document?.content?.referenceData?.find((entry: ReferenceDataEntry) => entry.superId === referenceData.superId)?.revisionId;
          const res = await api.PUT('/reference-data/', {
            body: { ...(referenceData as ReferenceDataPutBody), ...(baseRevisionId ? { baseRevisionId } : {}) },
          });
          const revisionId = (res?.data as { revisionId?: string } | undefined)?.revisionId;
          if (revisionId) save.revisionId = revisionId;
          return res;
        })
        .finally(() => {
          save.pending -= 1;
          if (save.pending === 0) tableSaves.delete(key);
        });
      save.queue = run;
      return run;
    },

    onError: (error) => {
      if (isRevisionMismatch(error)) {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          title: i18n('reference-data:save-conflict.title'),
          text: i18n('reference-data:save-conflict.message'),
        });
        return;
      }
      ServerErrorHandler(error);
    },

    onSuccess: (res, { document, referenceData }) => {
      const revisionId = (res?.data as { revisionId?: string } | undefined)?.revisionId;
      if (revisionId) {
        queryClient.setQueriesData<EditorDocument>({ queryKey: [...documentKeys.detailAll] }, (cached) =>
          cached?.content?.referenceData
            ? produce(cached, (draft) => {
                const entry = draft.content.referenceData.find((e: ReferenceDataEntry) => e.superId === referenceData.superId);
                if (entry) entry.revisionId = revisionId;
              })
            : cached
        );
      }
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.listAll],
      });
      invalidateOnValueSave(queryClient, document?.superId);
    },
  });
}

export function useUpdateReferenceTableCssClass() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      documentSuperId,
      referenceDataSuperId,
      cssClass,
    }: {
      documentSuperId: number | string;
      referenceDataSuperId: string;
      cssClass?: string;
    }) => {
      const res = await api.PATCH('/reference-data/{documentSuperId}/{referenceDataSuperId}/css-class', {
        params: { path: { documentSuperId: Number(documentSuperId), referenceDataSuperId } },
        body: { cssClass },
      });
      return res;
    },

    onMutate: async ({ documentSuperId, referenceDataSuperId, cssClass }) => {
      await queryClient.cancelQueries({
        queryKey: [...documentKeys.detailAll],
      });
      queryClient.setQueriesData(
        {
          queryKey: documentKeys.detailPrefix(documentSuperId),
        },
        (old: EditorDocument | undefined) => {
          if (!old) return old;
          const updated: EditorDocument = JSON.parse(JSON.stringify(old));
          const refEntry = updated.content?.referenceData?.find((r: ReferenceDataEntry) => r.superId === referenceDataSuperId);
          if (refEntry) {
            refEntry.cssClass = cssClass || undefined;
          }
          return updated;
        }
      );
    },

    onError: ServerErrorHandler,

    onSettled: (_, __) => {
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
    },
  });
}

export function useCreateReferenceData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ referenceData }: { document?: EditorDocument; referenceData: ReferenceDataValue }) => {
      const res = await api.POST('/reference-data/', { body: referenceData as ReferenceDataPostBody });
      return res;
    },

    onError: ServerErrorHandler,

    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
    },
  });
}

export function useDeleteReferenceData() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ documentId, referenceData }: { documentId: number | string; referenceData: ReferenceDataValue }) => {
      await api.DELETE('/reference-data/{documentSuperId}/{revisionId}', {
        params: {
          path: {
            documentSuperId: Number(documentId),
            revisionId: requireValue(referenceData.revisionId, 'cannot delete reference data without a revisionId'),
          },
        },
      });
    },

    onSuccess: (_) => {
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
    },

    onError: (error: unknown) => {
      if (!(error instanceof ApiError) || error.code !== 'REFERENCE_IN_USE') {
        ServerErrorHandler(error);
      }
    },
  });
}

interface ReferenceListOptions {
  keepPreviousList?: boolean;
}

export const referenceListKey = (document?: EditorDocument) =>
  referenceDataKeys.list(document?.superId, (document?.content?.referenceData ?? []).map((ref: ReferenceDataEntry) => ref.revisionId).join(','));

export function useReferenceList(document?: EditorDocument, options?: ReferenceListOptions) {
  const CHUNK_THRESHOLD = 100;
  const CHUNK_SIZE = 50;
  return useQuery({
    queryKey: referenceListKey(document),
    ...(options?.keepPreviousList ? { placeholderData: keepPreviousData } : {}),
    staleTime: Infinity,

    queryFn: async () => {
      if (!Array.isArray(document?.content?.referenceData)) {
        return Promise.resolve([]);
      }
      const revisionIds = document.content.referenceData.map((ref: ReferenceDataEntry) => ref.revisionId);
      if (revisionIds.length === 0) return [];
      if (revisionIds.length <= CHUNK_THRESHOLD) {
        const { data } = await api.POST('/reference-data/batch', { body: { revisionIds, documentSuperId: document.superId } });
        // The batch response body is loose in the contract; resolve it to reference-data values.
        return (data ?? []) as ReferenceDataValue[];
      } else {
        const chunks: string[][] = [];
        for (let i = 0; i < revisionIds.length; i += CHUNK_SIZE) {
          chunks.push(revisionIds.slice(i, i + CHUNK_SIZE));
        }

        let allReferenceData: ReferenceDataValue[] = [];

        for (let i = 0; i < chunks.length; i++) {
          const chunkIds = chunks[i];
          const { data } = await api.POST('/reference-data/batch', { body: { revisionIds: chunkIds, documentSuperId: document.superId } });
          allReferenceData = [...allReferenceData, ...((data ?? []) as ReferenceDataValue[])];
        }

        return allReferenceData;
      }
    },

    enabled: !!document,
  });
}

export function useReferenceTranslations(documentSuperId?: number | string) {
  return useQuery({
    queryKey: referenceDataKeys.forDocument(documentSuperId),

    queryFn: async () => {
      const { data } = await api.GET('/reference-data/xliff/metadata/{documentSuperId}', {
        params: { path: { documentSuperId: Number(documentSuperId) } },
      });
      return data;
    },

    enabled: !!documentSuperId,
  });
}

export function usePreviewXliff(documentSuperId?: number | string) {
  return useMutation({
    mutationFn: async (xliffId: string) => {
      const { data } = await api.POST('/reference-data/xliff/{documentSuperId}/preview', {
        params: {
          path: { documentSuperId: Number(requireValue(documentSuperId, 'previewXliff ran before the document loaded')) },
          query: { xliffId },
        },
      });
      return data;
    },

    onError: ServerErrorHandler,
  });
}

export function useUploadXliff(documentSuperId?: number | string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: referenceDataKeys.forDocument(documentSuperId),

    mutationFn: async ({ xliffId, skipSourceChanged = false }: { xliffId: string; skipSourceChanged?: boolean }) => {
      const { data } = await api.POST('/reference-data/xliff/{documentSuperId}', {
        params: {
          path: { documentSuperId: Number(requireValue(documentSuperId, 'uploadXliff ran before the document loaded')) },
          query: { xliffId, skipSourceChanged: String(skipSourceChanged) },
        },
      });
      return data;
    },

    // NOTE: legacy passed `enabled:` here — a no-op for react-query mutations; dropped during TS conversion.
    onError: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'error',
        text: i18n('reference-data:refresh-error'),
        removeTime: 3000,
      });
    },

    onSuccess: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        type: 'success',
        text: i18n('reference-data:refresh-success'),
        removeTime: 3000,
      });
      // Preserved legacy call: filter key is longer than the (revisionId-keyed) list key, so it never matches.
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.listAll, documentSuperId, undefined],
      });
    },
  });
}

export function useParsedSheet(data: XlsxFileData | undefined, selectedSheet: string | number | undefined, locale: string) {
  return useQuery({
    queryKey: referenceDataKeys.parsedSheet(data?.revisionId ?? '', selectedSheet, locale),

    queryFn: () => {
      return new Promise<(ReferenceCell | string)[][]>((resolve, reject) => {
        setTimeout(() => {
          try {
            resolve(parseXlsxData(data, selectedSheet, locale));
          } catch (e) {
            reject(e);
          }
        }, 0);
      });
    },

    retry: false,
    enabled: !!data && !!selectedSheet && !!locale,
    meta: {
      errorHandler: (error: unknown) => {
        if (error instanceof MissingSheet) return;
        console.error(error);
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n('reference-data:failed-parsing-xlsx'),
        });
      },
    },
  });
}

type SheetDataQuery = { isLoading: boolean; isSuccess: boolean; isError: boolean; data: ReferenceCell[][] };
type CellRangeSelection = { start: { i: number; j: number }; end: { i: number; j: number } };

export function useSelectedData(sheetData: SheetDataQuery, selection: CellRangeSelection | null | undefined, currentReferenceData: ReferenceDataValue) {
  // isIdle dropped: v5 has no idle status, so it would always read undefined. No consumer reads it.
  const status = {
    isLoading: sheetData.isLoading,
    isSuccess: sheetData.isSuccess,
    isError: sheetData.isError,
  };

  if (!sheetData.isSuccess) {
    return status;
  }

  if (!selection) {
    return {
      isLoading: false,
      isSuccess: false,
      isError: true,
    };
  }

  const startRow = Math.min(selection.start.i, selection.end.i);
  const endRow = Math.max(selection.start.i, selection.end.i) + 1;
  const startColumn = Math.min(selection.start.j, selection.end.j);
  const endColumn = Math.max(selection.start.j, selection.end.j) + 1;

  const selectedData = sheetData.data.slice(startRow, endRow).map((row) => row.slice(startColumn, endColumn).map((cell) => ({ ...cell })));

  const old = currentReferenceData.selectionContent;
  selectedData.forEach((row, y) => {
    row.forEach((cell, x) => {
      const oldCell = old?.[y]?.[x];
      cell.format = oldCell?.format;
      cell.classes = oldCell?.classes;
      cell.styling = oldCell?.styling;
      cell.background = oldCell?.background;
      cell.border = oldCell?.border;
      if (oldCell?.value === cell?.value) {
        cell.locales = oldCell?.locales;
      }
    });
  });

  return {
    ...status,
    data: selectedData,
  };
}

type MatchTagsBody = NonNullable<paths['/reference-data/match-tags']['post']['requestBody']>['content']['application/json'];
type MatchTagsResult = paths['/reference-data/match-tags']['post']['responses']['200']['content']['application/json'];

export function useMatchTagsForward() {
  return useMutation({
    mutationFn: async (body: MatchTagsBody): Promise<MatchTagsResult | undefined> => {
      const res = await api.POST('/reference-data/match-tags', { body });
      return res.data;
    },

    onError: ServerErrorHandler,
  });
}

export function useAddTagsToCells() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ referenceData, tagsToAdd }: { referenceData: ReferenceDataValue; tagsToAdd: { row: number; col: number; tagId: string }[] }) => {
      const res = await api.PATCH('/reference-data/{documentSuperId}/{referenceDataSuperId}/cell-tags', {
        params: {
          path: {
            documentSuperId: Number(referenceData.documentSuperId),
            referenceDataSuperId: requireValue(referenceData.superId, 'cannot tag cells of reference data without a superId'),
          },
        },
        body: { tagsToAdd },
      });
      return res;
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...referenceDataKeys.listAll],
      });
      queryClient.invalidateQueries({
        queryKey: [tagQueryKeys.tagsByDocument],
      });
      queryClient.invalidateQueries({
        queryKey: [tagQueryKeys.tagsByDocumentPaginated],
      });
    },
  });
}
